/**
 * A sticker says a whole dollar, so something has to decide which way.
 *
 * Left to itself the app would round the ordinary way and never mention it. A
 * vendor's margin lives in that decision though - a binder of two hundred cards
 * rounded down is real money left on the table, and rounded up is a table of
 * prices that all look slightly grabby. So it is theirs to make, per binder and
 * per card, and the totals are shown either way.
 */

export const ROUNDING_MODES = [
  { key: 'auto', label: 'Auto', hint: '.50 and up' },
  { key: 'up', label: 'All up', hint: 'never lose' },
  { key: 'down', label: 'All down', hint: 'easier sell' },
];

export const DEFAULT_MODE = 'auto';

const PREF_KEY = 'cc:labelPrinter:rounding';

/**
 * A price already at a whole dollar has nothing to decide.
 *
 * Null and empty string both pass through Number() as 0, which is finite and a
 * whole dollar - so an unpriced card reported itself as exact and would have
 * worn the badge on the rounding screen. Absence is checked before the maths,
 * not after.
 */
export function isExactDollar(price) {
  if (price === null || price === undefined || price === '') return false;
  const v = Number(price);
  return Number.isFinite(v) && Math.abs(v - Math.round(v)) < 0.005;
}

/**
 * What one card's sticker will say, and which way it went.
 *
 * @param {number} price
 * @param {'auto'|'up'|'down'} how
 * @returns {{ value: number|null, dir: 'up'|'down'|null, exact: boolean }}
 */
export function roundOne(price, how = DEFAULT_MODE) {
  const v = Number(price);
  if (price === null || price === undefined || price === '' || !Number.isFinite(v)) {
    return { value: null, dir: null, exact: false };
  }
  if (isExactDollar(v)) return { value: Math.round(v), dir: null, exact: true };
  if (how === 'up') return { value: Math.ceil(v), dir: 'up', exact: false };
  if (how === 'down') return { value: Math.floor(v), dir: 'down', exact: false };
  const rounded = Math.round(v);
  return { value: rounded, dir: rounded > v ? 'up' : 'down', exact: false };
}

/**
 * Every card's sticker price.
 *
 * `overrides` is by card id and beats the mode, so the two chase cards can
 * round up while the bulk rounds down - and a card someone has touched keeps
 * its direction when the mode changes, which is the entire reason it exists.
 *
 * @param {object[]} cards
 * @param {string} mode
 * @param {Record<string, 'up'|'down'>} overrides
 */
export function roundAll(cards, mode = DEFAULT_MODE, overrides = {}) {
  const rows = (cards || []).map((card) => {
    const how = overrides[card.id] || mode;
    return { card, ...roundOne(card.price, how), touched: !!overrides[card.id] };
  });
  const real = rows.reduce((sum, r) => sum + (Number(r.card.price) || 0), 0);
  const printed = rows.reduce((sum, r) => sum + (r.value || 0), 0);
  return { rows, real, printed, delta: printed - real };
}

/** The saved default, for a vendor who always rounds the same way. */
export function readMode() {
  try {
    const v = window.localStorage.getItem(PREF_KEY);
    return ROUNDING_MODES.some((m) => m.key === v) ? v : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

export function writeMode(mode) {
  try {
    if (mode) window.localStorage.setItem(PREF_KEY, mode);
    else window.localStorage.removeItem(PREF_KEY);
  } catch {
    /* a remembered default is a convenience, never a requirement */
  }
}
